import React, { useEffect } from 'react';
import css from './PopupShell.css';

/**
 * Shared visual chrome for popups/modals across the app — same design
 * language as the coach "Welcome to V-Coach" onboarding popup: gradient
 * header with glow + icon + badge, white body, footer action row.
 * Colors are driven by --marketplaceColor* custom properties, so the
 * gradient renders teal on client/V-Corp pages and purple on coach pages
 * automatically.
 *
 * Individual popups own their own content/logic and only render this shell
 * around it. Other popup components that don't use PopupShell directly
 * (e.g. ones built on the `reactjs-popup` library) can still import
 * `css` from './PopupShell.css' to reuse the `popupShellBtn*` button
 * classes documented there.
 */
const PopupShell = ({
  onClose,
  closeOnOverlayClick = true,
  ariaLabel,
  badge,
  badgeVariant = 'default',
  icon,
  iconVariant = 'default',
  title,
  subtitle,
  headerVariant = 'full',
  progress,
  maxWidth = 480,
  children,
  footer,
  className = '',
  bodyClassName = '',
  id,
}) => {
  useEffect(() => {
    if (!onClose) return undefined;
    const handleKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleOverlayClick = () => {
    if (closeOnOverlayClick && onClose) onClose();
  };

  const hasHeader = headerVariant !== 'none' && (title || badge || icon);

  return (
    <div
      className={css.popupShellOverlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
      id={id}
    >
      <div
        className={`${css.popupShellCard} ${className}`}
        style={{ maxWidth }}
        onClick={e => e.stopPropagation()}
      >
        {hasHeader && (
          <div className={headerVariant === 'compact' ? css.popupShellHeaderPlain : css.popupShellHeader}>
            {headerVariant !== 'compact' && <div className={css.popupShellHeaderGlow} />}
            {onClose && (
              <button
                type="button"
                className={css.popupShellCloseBtn}
                onClick={onClose}
                aria-label="Close"
              >
                &#x2715;
              </button>
            )}
            <div className={css.popupShellHeaderContent}>
              {icon && (
                <div
                  className={`${css.popupShellIconWrap} ${
                    iconVariant === 'danger' ? css.popupShellIconWrapDanger : ''
                  }`}
                >
                  {icon}
                </div>
              )}
              {badge && (
                <span
                  className={`${css.popupShellBadge} ${badgeVariant === 'gold' ? css.popupShellBadgeGold : ''}`}
                >
                  {badge}
                </span>
              )}
              {title && <h2 className={css.popupShellTitle}>{title}</h2>}
              {subtitle && <p className={css.popupShellSubtitle}>{subtitle}</p>}
            </div>
            {typeof progress === 'number' && (
              <div className={css.popupShellProgressWrap}>
                <div className={css.popupShellProgressLabels}>
                  <span className={css.popupShellProgressLabel}>Progress</span>
                  <span className={css.popupShellProgressValue}>{progress}%</span>
                </div>
                <div className={css.popupShellProgressTrack}>
                  <div className={css.popupShellProgressFill} style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {!hasHeader && onClose && (
          <button
            type="button"
            className={css.popupShellCloseBtn}
            style={{ top: 12, right: 12, background: 'rgba(15,23,42,0.06)', color: '#64748B' }}
            onClick={onClose}
            aria-label="Close"
          >
            &#x2715;
          </button>
        )}

        {children && <div className={`${css.popupShellBody} ${bodyClassName}`}>{children}</div>}
        {footer && <div className={css.popupShellFooter}>{footer}</div>}
      </div>
    </div>
  );
};

export default PopupShell;
